// Positive tool (key 2) — the citizen-care cursor. While POSITIVE mode is on
// at home, hovering a citizen FREEZES it and pops a small menu so the player
// can pay to better that cat on the spot instead of waiting for it to wander
// past the right building:
//   • School   → graduate now (earns more + immune to turning feral)
//   • Temple   → BLESS → Devout (immune to feral AND mends nearby injured cats)
//   • Hospital → heal it instantly (only if injured)
//
// The menu is sticky once opened (you don't have to keep hovering the cat to
// reach the buttons). It closes on a pick, on a click outside, on Escape, or
// when the tool mode flips away from POSITIVE.

import * as THREE from 'three'
import { PositiveToolConfig, BuildingTypes, HomeCare, RealEstatePrices } from '../config/balance.js'
import { homeCareCost } from '../game/hungerRules.js'
import { Events, on, emit } from '../ui/eventBus.js'
import { getToolMode, ToolModes } from '../game/toolMode.js'
import * as advManager from '../game/adventureManager.js'
import * as season from '../game/seasonManager.js'
import { getBuildings } from '../game/buildingState.js'
import { formatCoins } from '../ui/format.js'
import {
  getPeasantObjects, freezePeasant, unfreezePeasant,
  educatePeasant, blessPeasant, healPeasant,
} from './peasants.js'

let captured = null // the frozen citizen currently under the menu
let menuEl = null

// Active only at home while POSITIVE mode is on (not away on an adventure,
// where key 2 means "explore"). Temple needs its building; School and Hospital do not — without one it is HOME SCHOOLING / HOME
// HEALTHCARE at HomeCare's premium, and the menu says what a School / Hospital would save.
function active() {
  return getToolMode() === ToolModes.POSITIVE && !advManager.isAdventuring()
}

function hasBuilding(type) {
  return getBuildings().some((b) => b.type === type)
}

// What an action costs right now: schooling and healing carry the home-care premium until the building is built.
export function feeFor(action) {
  if (action === 'school') return homeCareCost(PositiveToolConfig.schoolFee, hasBuilding(BuildingTypes.SCHOOL), HomeCare.schoolMult)
  if (action === 'hospital') return homeCareCost(PositiveToolConfig.hospitalFee, hasBuilding(BuildingTypes.HOSPITAL), HomeCare.healthMult)
  return PositiveToolConfig.templeFee
}

export function createPositiveTool({ camera, canvas }) {
  if (!camera || !canvas) return
  const ray = new THREE.Raycaster()
  const pointer = new THREE.Vector2()

  buildMenu()
  if (import.meta.env?.DEV) window.__positive = { feeFor } // dev/test hook

  canvas.addEventListener('pointermove', (e) => {
    if (!active() || captured) return // sticky: keep the open menu until dismissed
    const roots = getPeasantObjects()
    if (!roots.length) { canvas.style.cursor = 'crosshair'; return }
    const rect = canvas.getBoundingClientRect()
    pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    ray.setFromCamera(pointer, camera)
    const hits = ray.intersectObjects(roots, true)
    if (!hits.length) { canvas.style.cursor = 'crosshair'; return }
    const rootSet = new Set(roots)
    let o = hits[0].object
    while (o && !rootSet.has(o)) o = o.parent
    if (o) capture(o, e.clientX, e.clientY)
  })

  // Dismiss the menu on an outside click / Escape / mode change.
  document.addEventListener('pointerdown', (e) => {
    if (captured && menuEl && !menuEl.contains(e.target)) release()
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && captured) release()
  })
  on(Events.ToolModeChange, () => { if (!active()) release() })
  on(Events.KingdomChange, release)
}

// Freeze the citizen + open the menu anchored near the cursor.
function capture(cat, clientX, clientY) {
  captured = cat
  freezePeasant(cat)
  renderMenu(cat)
  const pad = 14
  const w = menuEl.offsetWidth || 200
  const h = menuEl.offsetHeight || 150
  // Keep the menu on-screen (flip left/up near the right/bottom edges).
  let x = clientX + pad
  let y = clientY + pad
  if (x + w > window.innerWidth) x = clientX - w - pad
  if (y + h > window.innerHeight) y = clientY - h - pad
  menuEl.style.left = `${Math.max(4, x)}px`
  menuEl.style.top = `${Math.max(4, y)}px`
  menuEl.classList.add('show')
}

function release() {
  if (captured) unfreezePeasant(captured)
  captured = null
  if (menuEl) menuEl.classList.remove('show')
}

// ── Menu DOM ────────────────────────────────────────────────────────────────
function buildMenu() {
  if (menuEl) return
  menuEl = document.createElement('div')
  menuEl.className = 'positive-menu'
  document.body.appendChild(menuEl)
  menuEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action]')
    if (!btn || btn.disabled) return
    apply(btn.dataset.action)
  })
}

// Per-citizen menu — each row enabled only if the building exists, the action
// applies (e.g. Hospital only when injured), and the player can afford the fee.
function renderMenu(cat) {
  const wealth = season.getWealth()
  const u = cat.userData
  const rows = [
    {
      action: 'school', icon: '🎓', name: hasBuilding(BuildingTypes.SCHOOL) ? 'School' : 'Home schooling',
      sub: hasBuilding(BuildingTypes.SCHOOL) ? 'Educate — immune to feral' : `Educate at home — a School saves ${formatCoins(feeFor('school') - PositiveToolConfig.schoolFee)} each`,
      fee: feeFor('school'),
      ok: !u.educated,
      reason: u.educated ? 'Already educated' : null,
    },
    {
      action: 'temple', icon: '🛐', name: 'Temple',
      sub: 'Bless — Devout, heals others',
      fee: PositiveToolConfig.templeFee,
      ok: hasBuilding(BuildingTypes.TEMPLE) && !u.devout,
      reason: !hasBuilding(BuildingTypes.TEMPLE) ? 'Build a Temple' : u.devout ? 'Already devout' : null,
    },
    {
      action: 'hospital', icon: '✚', name: hasBuilding(BuildingTypes.HOSPITAL) ? 'Hospital' : 'Home healthcare',
      sub: hasBuilding(BuildingTypes.HOSPITAL) ? 'Heal the wounded' : `Heal at home — a Hospital saves ${formatCoins(feeFor('hospital') - PositiveToolConfig.hospitalFee)} each`,
      fee: feeFor('hospital'),
      ok: !!u.injured,
      reason: !u.injured ? 'Not injured' : null,
    },
  ]
  menuEl.innerHTML = `<div class="positive-menu__head">Tend this citizen</div>` + rows.map((r) => {
    const afford = wealth >= r.fee
    const disabled = !r.ok || !afford
    const note = r.reason ?? (!afford ? 'Need more coin' : `¥${formatCoins(r.fee)}`)
    return `<button data-action="${r.action}" ${disabled ? 'disabled' : ''}>
      <span class="positive-menu__icon">${r.icon}</span>
      <span class="positive-menu__text"><strong>${r.name}</strong><em>${r.sub}</em></span>
      <span class="positive-menu__fee">${note}</span>
    </button>`
  }).join('') + (!hasBuilding(BuildingTypes.SCHOOL) || !hasBuilding(BuildingTypes.HOSPITAL)
    ? `<div class="positive-menu__tip">${[!hasBuilding(BuildingTypes.SCHOOL) && `For more cost-effective education, build a School (${formatCoins(RealEstatePrices.SCHOOL)}).`, !hasBuilding(BuildingTypes.HOSPITAL) && `For more cost-effective healthcare, build a Hospital (${formatCoins(RealEstatePrices.HOSPITAL)}).`].filter(Boolean).join(' ')}</div>` : '')
}

function apply(action) {
  const cat = captured
  if (!cat) return
  const fee = feeFor(action)
  if (season.getWealth() < fee) return
  let done = false
  if (action === 'school') done = educatePeasant(cat)
  else if (action === 'temple') done = blessPeasant(cat)
  else if (action === 'hospital') done = healPeasant(cat)
  if (!done) { release(); return }
  season.spendWealth(fee)
  emit(Events.NarrativeBeat, {
    title: action === 'temple' ? 'A citizen is blessed 🛐'
      : action === 'school' ? 'A citizen graduates 🎓'
      : 'A citizen is healed ✚',
    body: action === 'temple'
      ? 'Devout now — immune to turning feral, and they mend the wounded they pass.'
      : action === 'school'
      ? 'Educated now — earns more, and the young one will never turn feral.'
      : 'Back to full health and full wages.',
  })
  release()
}
